import mainStyle from './port.module.scss';
import { ReactComponent as ProfileIcon } from "assets/images/icons/icn_dashboard_profile.svg";
import { useNavigate } from "react-router-dom";

import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep, isEqual } from "lodash";

// Hook
import useDataSourceManager from "hooks/useDataSourceManager";

// Component
import CollapseCard from "components/Collapse";
import { MenuSelect } from "components/Select/menuSelect";
import { RadioButton } from 'components/buttons/RadioButton';
import GlobalAlert from "components/Notification";
import Loading from "./loading";
import RemoveButton from './removeButton';
import BottomActionBar from "./bottomActionBar";
import ConfirmDeleteModal from "./confirmDeleteModal";

// Util
import {
  defaultValue,
  deviceCategoryOption,
  portTypeOption,
  linkSpeedOption,
  apiMappingKey,
} from "./util/const";
import Api from "./util/api";
import Form from "./util/form";

// Const
import { formLimitation } from 'const';

const PortContent = ({ auth, selectedPort = null, showEditGeneral, onDrawerApplyCallback = () => { } }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const api = Api.init(auth);
  const form = Form.init(theme);
  const { sendRequest, getData, dataMap } = useDataSourceManager();

  // State
  const [loading, setLoading] = useState(true);
  const [deviceType, setDeviceType] = useState(null);
  const [originFormValue, setOriginFormValue] = useState(null);
  const [formValue, setFormValue] = useState(null);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [uuidList, setUuidList] = useState({
    "update": uuidv4(),
    "delete": uuidv4(),
  });
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });

  // Method
  const getDeviceTypeLabel = (deviceCategory) => {
    switch (deviceCategory) {
      case deviceCategoryOption.switch.value:
        return deviceCategoryOption.switch.label;
      case deviceCategoryOption.ap.value:
        return deviceCategoryOption.ap.label;
      default:
        return "";
    }
  }

  const handleChangeFormValue = (key, value) => {
    setFormValue((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  const handleHoverDeleteButton = (isHovered) => {
    setIsDeleteHovered(isHovered);
  }

  const handleOpenRemoveModal = () => {
    setOpenRemoveModal(true);
  }

  const resetFormValue = () => {
    setFormValue(cloneDeep(originFormValue));
  }

  const checkDataNotChanged = () => {
    let flag = true;
    for (const key in apiMappingKey) {
      const originValue = originFormValue[key].toString();
      const currentValue = formValue[key].toString();
      if (!isEqual(originValue, currentValue)) {
        flag = false;
        break;
      }
    }
    return flag;
  }

  const checkFormValid = () => {
    if (!formValue.profileName || formValue.profileName.trim() === "") {
      return false;
    }

    if (formValue.portType === portTypeOption.trunk.value
      && (
        formValue.allowVlan?.trim().length === 0
        || !form.checkAllowVlans(formValue.allowVlan?.trim())
      )
    ) {
      return false;
    }

    if (!form.checkPvid(formValue.pvid?.toString().trim())) {
      return false;
    }

    return true;
  }

  const handleApply = () => {
    setLoading(true);
    const payload = defaultValue.makePayload(formValue);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.update, formValue.id);
    sendRequest(
      apiKey,
      uuidList.update,
      params,
      payload,
    );
  }

  const handleDelete = () => {
    setOpenRemoveModal(false);
    setLoading(true);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.delete, formValue.id);
    sendRequest(
      apiKey,
      uuidList.delete,
      params,
    );
  }

  // Side effect
  useEffect(() => {
    if (selectedPort) {
      const updatedPort = {};
      for (const key in apiMappingKey) {
        const apiKey = apiMappingKey[key];
        updatedPort[key] = selectedPort[apiKey];
      }

      // Backward compatible for fiberLinkSpeed
      if (!updatedPort.fiberLinkSpeed) {
        updatedPort.fiberLinkSpeed = linkSpeedOption.auto.value;
      }

      setOriginFormValue(cloneDeep(updatedPort));
      setFormValue(cloneDeep(updatedPort));
      setDeviceType(selectedPort.deviceCategory);
      setLoading(true);
    } else {
      setFormValue(null);
    }
  }, [selectedPort]);

  useEffect(() => {
    if (loading) {
      setTimeout(() => {
        setLoading(false);
      }, 0);
    }
  }, [loading]);

  useEffect(() => {
    const updateResponse = getData(uuidList.update);
    if (updateResponse) {
      if (updateResponse.hasOwnProperty("code") && updateResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: updateResponse?.message, // TODO: not define error message format
        });
        setLoading(false);
      } else {
        onDrawerApplyCallback();
      }
    }

    const deleteResponse = getData(uuidList.delete);
    if (deleteResponse) {
      if (deleteResponse.hasOwnProperty("code") && deleteResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: deleteResponse?.message, // TODO: not define error message format
        });
        setLoading(false);
      } else {
        onDrawerApplyCallback();
      }
    }
  }, [dataMap]);

  const containerId = `profilePortContainer_${selectedPort?.id || "default"}`;

  return (
    <Box>
      {loading ? (
        <Loading />
      ) : (
        <>
          <Box id={containerId} sx={form.getFormStyle()}>
            <Box sx={form.getFormHeaderStyle()}>
              <ProfileIcon />
              <Box sx={{ marginLeft: '10px' }}>
                <Typography
                  sx={{
                    color: theme.palette.primary.greyPrimary,
                    fontSize: theme.typography.customFontSize[5],
                    fontWeight: theme.typography.customFontWeight[3],
                  }}>
                  {formValue ? formValue.profileName : ""}
                </Typography>
                <Box display={'flex'} alignItems="center">
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      border: `1.5px solid ${theme.palette.primary.main}`,
                      padding: "4px 12px",
                      height: "27px",
                      fontSize: theme.typography.customFontSize[0],
                      borderRadius: theme.shape.customBorderRadius[7],
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      display: "flex",
                    }}
                  >
                    {getDeviceTypeLabel(formValue.deviceCategory)}
                  </Box>
                  <Box display={'flex'} alignItems={'center'} gap={1} sx={{ marginLeft: '10px' }}>
                    <Box>Assigned</Box>
                    <Box
                      sx={{
                        color: theme.palette.primary.main,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        ":hover": {
                          textDecoration: 'none',
                        },
                      }}
                      onClick={() => {
                        navigate("/site/settings", {
                          state: {
                            navigateTab: "wired-port-management",
                            navigatePortProfileName: formValue.profileName || "",
                            navigatePortDeviceType: deviceCategoryOption[deviceType].tabName.portManagement
                          }
                        });
                      }}
                    >
                      {formValue.assignedPort?.length} Port(s)
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={form.getFormBodyStyle()}>
              {
                deviceType === deviceCategoryOption.switch.value && (
                  <>
                    <CollapseCard title="Basic" >
                      <Box className={mainStyle["section-container"]}>
                        {/* Profile Name */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Profile Name
                            </Typography>
                            <TextField
                              placeholder="e.g. My Office"
                              value={formValue.profileName || ""}
                              onChange={(e) => handleChangeFormValue("profileName", e.target.value)}
                              sx={form.getTextfieldStyle(true)}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        {/* Port State  */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Port State
                            </Typography>
                            <RadioButton
                              label="Enabled"
                              value={true}
                              checked={formValue.portState === true}
                              onChange={() => handleChangeFormValue("portState", true)}
                              name="portState"
                              disabled={!showEditGeneral}
                            />
                            <RadioButton
                              label="Disabled"
                              value={false}
                              checked={formValue.portState === false}
                              onChange={() => handleChangeFormValue("portState", false)}
                              name="portState"
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        {/* Port Type */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Port Type
                            </Typography>
                            {
                              portTypeOption.convertToForm().map((item, index) => (
                                <RadioButton
                                  key={index}
                                  label={item.label}
                                  value={item.value}
                                  checked={formValue.portType === item.value}
                                  onChange={() => {
                                    handleChangeFormValue("allowVlan", originFormValue.allowVlan);
                                    handleChangeFormValue("portType", item.value);
                                  }}
                                  name="portType"
                                  disabled={!showEditGeneral}
                                />
                              ))
                            }
                          </Box>
                        </Box>
                        {/* PVID */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              PVID
                            </Typography>
                            <TextField
                              sx={form.getTextfieldStyle(form.checkPvid(formValue.pvid?.toString().trim()))}
                              value={formValue.pvid}
                              onChange={(e) => { handleChangeFormValue("pvid", e.target.value); }}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        <Box className={mainStyle["tip_section"]}>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                            </Typography>
                            <Typography
                              sx={{
                                color: theme.palette.primary.greySecound,
                                fontSize: theme.typography.customFontSize[1],
                              }}
                            >
                              (1~4094)
                            </Typography>
                          </Box>
                        </Box>
                        {/* Allow VLAN */}
                        {
                          formValue.portType === portTypeOption.trunk.value && (
                            <>
                              <Box>
                                <Box className={mainStyle["row-style"]}>
                                  <Typography className={mainStyle["collapse-typography"]}>
                                    Allow VLAN
                                  </Typography>
                                  <TextField
                                    placeholder="e.g. 1,4-7"
                                    sx={form.getTextfieldStyle(form.checkAllowVlans(formValue.allowVlan?.trim()))}
                                    value={formValue.allowVlan}
                                    onChange={(e) => handleChangeFormValue("allowVlan", e.target.value)}
                                    disabled={!showEditGeneral}
                                  />
                                </Box>
                              </Box>
                              <Box className={mainStyle["tip_section"]}>
                                <Box className={mainStyle["row-style"]}>
                                  <Typography className={mainStyle["collapse-typography"]}>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: theme.palette.primary.greySecound,
                                      fontSize: theme.typography.customFontSize[1],
                                    }}
                                  >
                                    {/* (1~4094) */}
                                    {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                                  </Typography>
                                </Box>
                              </Box>
                            </>
                          )
                        }
                        {/* Copper Link Speed */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Copper Link Speed
                            </Typography>
                            <MenuSelect
                              value={formValue.linkSpeed}
                              onChange={(opt) => {
                                handleChangeFormValue("linkSpeed", opt.value);
                              }}
                              options={linkSpeedOption.convertToForm()}
                              width={form.getTextfieldStyle().width}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        {/* Fiber Link Speed */}
                        <Box>
                          <Box className={mainStyle["row-style"]} style={{ marginTop: '10px' }}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Fiber Link Speed
                            </Typography>
                            <MenuSelect
                              value={formValue.fiberLinkSpeed}
                              onChange={(opt) => {
                                handleChangeFormValue("fiberLinkSpeed", opt.value);
                              }}
                              options={linkSpeedOption.convertToForm()}
                              width={form.getTextfieldStyle().width}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </CollapseCard>
                    <CollapseCard title="PoE" >
                      <Box className={mainStyle["section-container"]}>
                        {/* PoE State */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              PoE State
                            </Typography>
                            <RadioButton
                              label="Enabled"
                              value={true}
                              checked={formValue.poeState === true}
                              onChange={() => handleChangeFormValue("poeState", true)}
                              name="poeState"
                              disabled={!showEditGeneral}
                            />
                            <RadioButton
                              label="Disabled"
                              value={false}
                              checked={formValue.poeState === false}
                              onChange={() => handleChangeFormValue("poeState", false)}
                              name="poeState"
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </CollapseCard>
                    <CollapseCard title="Advance" >
                      <Box className={mainStyle["section-container"]}>
                        {/* DDP State */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              DDP State
                            </Typography>
                            <RadioButton
                              label="Enabled"
                              value={true}
                              checked={formValue.ddpState === true}
                              onChange={() => handleChangeFormValue("ddpState", true)}
                              name="ddpState"
                              disabled={!showEditGeneral}
                            />
                            <RadioButton
                              label="Disabled"
                              value={false}
                              checked={formValue.ddpState === false}
                              onChange={() => handleChangeFormValue("ddpState", false)}
                              name="ddpState"
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        {/* LBD State */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              LBD State
                            </Typography>
                            <RadioButton
                              label="Enabled"
                              value={true}
                              checked={formValue.lbdState === true}
                              onChange={() => handleChangeFormValue("lbdState", true)}
                              name="lbdState"
                              disabled={!showEditGeneral}
                            />
                            <RadioButton
                              label="Disabled"
                              value={false}
                              checked={formValue.lbdState === false}
                              onChange={() => handleChangeFormValue("lbdState", false)}
                              name="lbdState"
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </CollapseCard>
                  </>
                )
              }
              {
                deviceType === deviceCategoryOption.ap.value && (
                  <>
                    <CollapseCard title="Basic" >
                      <Box className={mainStyle["section-container"]}>
                        {/* Profile Name */}
                        <Box style={{ marginBottom: '16px' }}>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Profile Name
                            </Typography>
                            <TextField
                              placeholder="e.g. My Office"
                              value={formValue.profileName || ""}
                              onChange={(e) => handleChangeFormValue("profileName", e.target.value)}
                              sx={form.getTextfieldStyle(true)}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        {/* PVID */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              PVID
                            </Typography>
                            <TextField
                              sx={form.getTextfieldStyle(form.checkPvid(formValue.pvid?.toString().trim()))}
                              value={formValue.pvid}
                              onChange={(e) => { handleChangeFormValue("pvid", e.target.value); }}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        <Box className={mainStyle["tip_section"]}>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                            </Typography>
                            <Typography
                              sx={{
                                color: theme.palette.primary.greySecound,
                                fontSize: theme.typography.customFontSize[1],
                              }}
                            >
                              (1~4094)
                            </Typography>
                          </Box>
                        </Box>
                        {/* Allow VLAN */}
                        <Box>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              Allow VLAN
                            </Typography>
                            <TextField
                              placeholder="e.g. 1,4-7"
                              sx={form.getTextfieldStyle(form.checkAllowVlans(formValue.allowVlan?.trim()))}
                              value={formValue.allowVlan}
                              onChange={(e) => handleChangeFormValue("allowVlan", e.target.value)}
                              disabled={!showEditGeneral}
                            />
                          </Box>
                        </Box>
                        <Box className={mainStyle["tip_section"]}>
                          <Box className={mainStyle["row-style"]}>
                            <Typography className={mainStyle["collapse-typography"]}>
                            </Typography>
                            <Typography
                              sx={{
                                color: theme.palette.primary.greySecound,
                                fontSize: theme.typography.customFontSize[1],
                              }}
                            >
                              {/* (1~4094) */}
                              {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CollapseCard>
                  </>
                )
              }
              <RemoveButton
                label="Remove Port Profile"
                onHoverDeleteButton={handleHoverDeleteButton}
                isDeleteHovered={isDeleteHovered}
                onOpenRemoveModal={handleOpenRemoveModal}
                showEditGeneral={showEditGeneral}
              />
            </Box>
            <BottomActionBar
              parentId={containerId}
              show={!checkDataNotChanged()}
              onCheckFormValid={checkFormValid}
              onCancel={resetFormValue}
              onApply={handleApply}
            />
          </Box>
          <ConfirmDeleteModal
            removeModal={openRemoveModal}
            onOpenRemoveModal={setOpenRemoveModal}
            label={formValue ? formValue.profileName : ""}
            removeCallback={handleDelete}
          />
          <GlobalAlert
            open={alertInfo.open}
            severity={alertInfo.severity}
            message={alertInfo.message}
            onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
          />
        </>
      )}
    </Box>
  );
}

export default PortContent;
